import { Card, LinkOutSuggestion, List, ListItem, Response, ResponseOutput } from "stentor-models";
import { ChatMessageRequest, ChatServerActionLink, ChatServerCard, ChatServerList, ChatServerListItem, PermissionRequestType } from "../xapp/ChatServerMessage";

export function convertFromListDisplay(list: List): ChatServerList {
    return {
        type: list.type,
        title: list.title,
        items: list.items.map(item => {
            const responseItem: ChatServerListItem = {
                title: item.title,
                subTitle: item.description,
                token: item.token,
                url: item.url,
                imageUrl: item.image?.url,
                imageActionUrl: item.image?.imageActionUrl
            };

            // TODO: Remove any when the buttons are defined on the list (ListButton - title + openUrlAction)
            const itemButtons = item.buttons;

            if (itemButtons && itemButtons.length > 0) {
                responseItem.buttons = itemButtons.map(button => ({
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    actionUrl: (button as any).openUrlAction,
                    label: button.title
                }));
            }

            return responseItem;
        })
    };
}


export function convertToListDisplay(list: ChatServerList): List {
    return {
        type: list.type,
        title: list.title,
        items: (list.items || []).map(item => {
            const responseItem: ListItem = {
                title: item.title,
                description: item.subTitle,
                token: item.token,
                url: item.url,
                image: {
                    url: item.imageUrl,
                    imageActionUrl: item.imageActionUrl,
                    accessibilityText: ""
                }
            };

            // TODO: Remove any when the buttons are defined on the list (ListButton - title + openUrlAction)
            const itemButtons = item.buttons;

            if (itemButtons && itemButtons.length > 0) {
                responseItem.buttons = itemButtons.map(button => ({
                    openUrlAction: button.actionUrl,
                    title: button.label
                }));
            }

            return responseItem;
        })
    };
}


export function convertFromCardDisplay(card: Card): ChatServerCard {
    return {
        content: card.content,
        imageUrl: card.smallImageUrl,
        title: card.title,
        imageActionUrl: card?.imageActionUrl,
        buttons: card.buttons ? card.buttons.map(button => ({
            actionUrl: button.openUrlAction,
            label: button.title
        })) : undefined
    };
}

export function convertToCardDisplay(card: ChatServerCard): Card {
    return {
        type: "CARD",
        content: card.content,
        smallImageUrl: card.imageUrl,
        title: card.title,
        imageActionUrl: card?.imageActionUrl,
        buttons: card.buttons ? card.buttons.map(button => ({
            openUrlAction: button.actionUrl,
            title: button.label
        })) : undefined
    };
}

function getOutput(value: string | ResponseOutput | undefined): ResponseOutput | undefined {
    if (value) {
        if (typeof value === "string") {
            return {
                displayText: value
            };
        } else {
            return value;
        }
    }
    return undefined;
}

/**
 * Converts a Stentor Response to a ChatMessageRequest
 * 
 * @param botResponse 
 * @param now 
 * @returns 
 */
export function responseToMessage(botResponse: Response, now: number = new Date().getTime()): ChatMessageRequest {

    let responseMessage: ChatMessageRequest;

    if (!botResponse) {
        return responseMessage;
    }

    let text: string;
    let html: string;
    let endSession: boolean;

    const outputSpeech = getOutput(botResponse.outputSpeech);
    const reprompt = getOutput(botResponse.reprompt);

    if (outputSpeech) {
        text = outputSpeech.displayText;
        html = outputSpeech.html;
    }

    if (botResponse.system === "TRANSFER_CALL") {
        responseMessage = {
            type: "handOff",
            timestamp: now,
            handoffMessage: text,
            handoffTarget: botResponse?.data?.transferPhoneNumber as string,
            endSession: false,
            user: undefined
        };
    } else if (botResponse.system?.startsWith("PERMISSION_")) {
        responseMessage = getPermissionResponse(botResponse, now);
    } else {
        endSession = !reprompt || !(reprompt.displayText || reprompt.ssml);

        responseMessage = {
            type: "msg",
            timestamp: now,
            msg: {
                displays: botResponse.displays,
                context: botResponse.context?.active?.map(ctx => ctx.name)
            },
            endSession,
            user: undefined
        };

        if (text && !html) {
            responseMessage.msg = {
                ...responseMessage.msg,
                text
            }
        }

        if (html) {
            responseMessage.msg = {
                ...responseMessage.msg,
                html
            }
        }

        if (outputSpeech?.suggestions?.length) {
            responseMessage.msg.options = outputSpeech.suggestions.map<string | ChatServerActionLink>(suggestion => {
                if (typeof suggestion === "string") {
                    // Simple chips (strings)
                    return suggestion;
                } else {
                    // "call out" chips
                    return {
                        label: suggestion.title,
                        actionUrl: (suggestion as LinkOutSuggestion).url
                    };
                }
            });

        }
    }

    return responseMessage;

}

function getPermissionRequestType(type: string): PermissionRequestType {
    switch (type) {
        case "PERMISSION_EMAIL":
            return "EMAIL";
        case "PERMISSION_LOCATION_PRECISE":
            return "LOCATION_PRECISE";
        default:
            throw new Error(`Unsupported permission: ${type}`);
    }
}

function getPermissionResponse(botResponse: Response, now: number): ChatMessageRequest {

    const type = getPermissionRequestType(botResponse.system);

    const outputSpeech = getOutput(botResponse.outputSpeech);

    const permissionPrimerAccepted = botResponse.data.permissionPrimerAccepted;
    const permissionDenied = botResponse.data.permissionDenied;

    return {
        type: "permissionRequest",
        timestamp: now,
        msg: {
            text: outputSpeech?.displayText ?? `${botResponse.data.permissionRequestTTSContext}`,
            permissionRequest: {
                time: now,
                type,
                approve: typeof permissionPrimerAccepted === "object" ? responseToMessage({
                    outputSpeech: permissionPrimerAccepted
                }).msg : undefined,
                deny: typeof permissionDenied === "object" ? responseToMessage({
                    outputSpeech: permissionDenied
                }).msg : undefined
            }
        },
        endSession: false,
        user: undefined //todo: set
    };

}
